import { HttpErrorResponse, HttpEvent, HttpEventType, HttpStatusCode } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'

import { EMPTY, Observable, catchError, mergeMap, of } from 'rxjs'

import { ComposerFileUploadEvent } from '@flowx/designer/modules/ai-builder/types/composer-file.types'
import { COMPOSER_FILE_TEXTS } from '@flowx/designer/modules/ai-builder/utils/composer-files.dictionary'
import { problemMessage } from '@flowx/designer/modules/ai-builder/utils/harness-error.util'
import {
  DocumentPluginApi,
  TempFileUploadResponse,
} from '@flowx/designer/shared/api/document-plugin.api'

/**
 * Sends one composer file on its way as soon as it is picked and reports how far it has got. The
 * bytes go up as a document-plugin temp file, and that is the whole of the browser's part: the
 * harness puts the file in the knowledge base and reads it once the message is sent.
 */
@Injectable({ providedIn: 'root' })
export class ComposerFileUploaderService {
  #documents = inject(DocumentPluginApi)

  upload(file: File, workspaceId: string | undefined): Observable<ComposerFileUploadEvent> {
    return this.#documents.uploadTempFileWithProgress(file, workspaceId).pipe(
      mergeMap((event) => this.#toUploadEvents(event)),
      catchError((error: unknown) =>
        of({
          type: 'failed',
          error: uploadError(error),
          recoverable: isRecoverable(error),
        } satisfies ComposerFileUploadEvent)
      )
    )
  }

  #toUploadEvents(event: HttpEvent<TempFileUploadResponse>): Observable<ComposerFileUploadEvent> {
    if (event.type === HttpEventType.UploadProgress) {
      return of({
        type: 'progress',
        percent: Math.round((100 * event.loaded) / (event.total || event.loaded || 1)),
      })
    }

    if (event.type !== HttpEventType.Response) {
      return EMPTY
    }

    return event.body?.id
      ? of({ type: 'uploaded', tempFileId: event.body.id } satisfies ComposerFileUploadEvent)
      : of({
          type: 'failed',
          error: COMPOSER_FILE_TEXTS.uploadFailed,
          recoverable: true,
        } satisfies ComposerFileUploadEvent)
  }
}

/** An upload failure as the chip says it: document-plugin's own words when it gave some, else
 * "Upload failed" for a failure another try may get past, and "Can't read this file" for one
 * it won't. */
function uploadError(error: unknown): string {
  return (
    problemMessage(error) ??
    (isRecoverable(error) ? COMPOSER_FILE_TEXTS.uploadFailed : COMPOSER_FILE_TEXTS.unreadable)
  )
}

/** Below 500, the statuses that say the connection or the gateway gave up, not that the file is
 * wrong. */
const TRANSIENT_STATUSES: ReadonlySet<number> = new Set([
  0,
  HttpStatusCode.RequestTimeout,
  HttpStatusCode.TooManyRequests,
])

/** Whether the same bytes may land on a second try: the connection or the server failed, not the
 * file. */
function isRecoverable(error: unknown): boolean {
  return (
    !(error instanceof HttpErrorResponse) ||
    TRANSIENT_STATUSES.has(error.status) ||
    error.status >= HttpStatusCode.InternalServerError
  )
}
